import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'underscore';

import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { TooltipChartType } from '../constants';
import { TooltipCardSection, TooltipSectionContentItem, TooltipSectionEventItem } from './tooltip-card-section';
import { formatCivilDate, formatCurrency } from '../utils/format-utils';
import { getKeyDataItemsConfig } from '../utils';

const { BudgetEventType, BudgetChangeType } = Constants;

export const TooltipCardUnstyled = ({
  i18n,
  chartType,
  currencyCode,
  civilDate,
  tooltipKeyData,
  topSpendingCampaigns,
  hourlyTrendChart,
  monitoringConfigs,
}) => {
  const keyDataSection = useMemo(() => {
    const keyDataItemsConfig = getKeyDataItemsConfig({ i18n, chartType });
    return keyDataItemsConfig && (
      <TooltipCardSection
        name="key-data"
        header={formatCivilDate({ i18n, civilDate, options: { skeleton: 'yMd' } })}
      >
        <div>
          {_.compact(_.map(keyDataItemsConfig, keyDataItem =>
          _.isNumber(tooltipKeyData[keyDataItem.valueFieldName]) && (
            <TooltipSectionContentItem
              key={`chartType-${chartType}-key-data-${keyDataItem.valueFieldName}`}
              title={keyDataItem.title}
              value={
                formatCurrency({
                  i18n,
                  value: tooltipKeyData[keyDataItem.valueFieldName],
                  currencyCode,
              })}
            />
            )))}
        </div>
      </TooltipCardSection>
    );
  }, [chartType, civilDate, currencyCode, i18n, tooltipKeyData]);

  const topSpendingCampaignsSection = useMemo(
    () =>
      !_.isEmpty(topSpendingCampaigns) && (
      <TooltipCardSection
        name="top-spending-campaigns"
        header={i18n.getString(_TL_('Top spending campaigns'))}
      >
        {_.map(topSpendingCampaigns, (topSpendingCampaign, index) => (
          <TooltipSectionContentItem
            key={`chartType-${chartType}-top-spending-campaign-${index}`}
            title={topSpendingCampaign.CampaignName}
            value={
              formatCurrency({
                i18n,
                value: topSpendingCampaign.DailySpend,
                currencyCode,
            })}
          />
          ))}
      </TooltipCardSection>
      )
    , [chartType, currencyCode, i18n, topSpendingCampaigns]
  );

  const hourlyTrendSection = useMemo(() =>
    hourlyTrendChart && (
    <TooltipCardSection
      name="hourly-trend"
      header={i18n.getString(_TL_('Hourly trend'))}
    >
      {hourlyTrendChart}
    </TooltipCardSection>
    ), [hourlyTrendChart, i18n]);

  const monitoringEventSection = useMemo(() => (
    monitoringConfigs && (
      <TooltipCardSection
        name="monitoring-events"
        header={i18n.getString(_TL_('Events'))}
      >
          {_.map(monitoringConfigs.BudgetChange, budgetChange => (
            <TooltipSectionEventItem
              key={`budget-change-${budgetChange.type}`}
              i18n={i18n}
              currencyCode={currencyCode}
              budgetChangeType={budgetChange.type}
              eventHistory={budgetChange.eventHistory}
            />
        ))}
          {_.map(monitoringConfigs.AbnormalStatus, abnormal => (
            <TooltipSectionEventItem
              key={`abnormal-status-chartType-${chartType}-${abnormal}`}
              i18n={i18n}
              currencyCode={currencyCode}
              budgetEventType={abnormal}
            />
        ))}
      </TooltipCardSection>
    )
  ), [chartType, currencyCode, i18n, monitoringConfigs]);

  return (
    <div className={classnames('tooltip-card-container', `tooltip-card-chartType-${chartType}`)}>
      {keyDataSection}
      {topSpendingCampaignsSection}
      {hourlyTrendSection}
      {monitoringEventSection}
    </div>
  );
};

TooltipCardUnstyled.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    formatCivilDate: PropTypes.func.isRequired,
  }).isRequired,
  currencyCode: PropTypes.string.isRequired,
  chartType: PropTypes.oneOf(_.values(TooltipChartType)).isRequired,
  civilDate: PropTypes.shape({}),
  tooltipKeyData: PropTypes.shape({}),
  topSpendingCampaigns: PropTypes.arrayOf(PropTypes.shape({
    CampaignName: PropTypes.string,
    DailySpend: PropTypes.number,
  })),
  hourlyTrendChart: PropTypes.node,
  monitoringConfigs: PropTypes.shape({
    BudgetChange: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.oneOf(_.values(BudgetChangeType)),
      eventHistory: PropTypes.shape({
        FieldName: PropTypes.string,
        NewValue: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        OldValue: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
        DailyClickChange: PropTypes.number,
      }),
    })),
    AbnormalStatus: PropTypes.arrayOf(PropTypes.oneOf(_.values(BudgetEventType))),
  }),
};

TooltipCardUnstyled.defaultProps = {
  civilDate: null,
  tooltipKeyData: null,
  topSpendingCampaigns: null,
  hourlyTrendChart: null,
  monitoringConfigs: null,
};
